import ExcelJS from 'exceljs';
import { translations } from '../i18n/translations';
import { getFullFileName, validateWindowsFileName } from './fileOperations';
import { calculateSiglatureAndMorsetti, calculateCableQuantities, calculateTerminalQuantities, calculateSheathQuantities } from './quantities';
import { getSiglaturaPerStampa, replaceArrowsForPDF, formatSezione, getStandardCableSideSiglature, normalizeStandardCable, getMultipolarLabel } from './cableLogic';
import { extractSiglaturaFromCollegamento } from './acPowerLogic';
import { getColorSwatchSegments, normalizeColorToCanonical, translateColorForDisplay } from './colorUtils';
import { PDF_CONSTANTS, readSVGHeightFromDOM, estimateCableDetailHeight } from './pdfHeightEstimation';
import { withEW3035Settings } from '../machines/EW-3035+L';
import {
    calculateLogoRenderSize,
    getPdfImageFormat,
    rasterizeSvgToPngDataUrl,
    resolvePdfLogoAsset
} from './logoUtils';


// ==================== FUNZIONI DI ESPORTAZIONE SIGLATURA ====================

export const exportSiglature = async (cables, settings, setWarnings, setSuccessMessage) => {
    settings = withEW3035Settings(settings);
    const language = settings?.lingua || 'it';
    const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
            if (text && typeof text === 'object' && k in text) {
                text = text[k];
            } else {
                return key;
            }
        }
        if (typeof text === 'string' && params) {
            return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
    };

    const fileNameValidation = validateWindowsFileName(settings.fileName);
    if (!fileNameValidation.isValid) {
        setWarnings(prev => [
            ...prev,
            t(fileNameValidation.reason === 'empty'
                ? 'fileOperations.noFileNameWarning'
                : 'fileOperations.invalidWindowsFileNameWarning')
        ]);
        return;
    }

    try {
        // VALIDA I CAVI E GENERA WARNING PER CAMPI MANCANTI
        const { validateCableFields } = await import('./validation');
        const cablesWithMissingFields = [];

        cables.forEach((cable, index) => {
            const missingFields = validateCableFields(cable, cables, settings.lingua);
            if (missingFields.length > 0) {
                cablesWithMissingFields.push({
                    index: index + 1,
                    missingFields: missingFields
                });
            }
        });

        if (cablesWithMissingFields.length > 0) {
            const warningMessages = cablesWithMissingFields.map(cable =>
                `Cavo #${cable.index}: ${cable.missingFields.join(', ')}`
            );
            setWarnings(prev => [
                ...prev,
                t('fileOperations.cablesWithMissingFields', { count: cablesWithMissingFields.length, fields: warningMessages.join('; ') })
            ]);
        }

        // OTTIENI I DATI CALCOLATI
        const allResults = calculateSiglatureAndMorsetti(cables, settings);

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Siglature');

        // ==================== HEADER PRINCIPALE (GRIGIO) ====================

        // Genera i codici
        const headerCodes = generateHeaderCodes(settings.fileName);
        const revisionFormatted = settings.revision.toString().padStart(2, '0');

        // Prima riga header - ALTEZZA 38.25
        sheet.mergeCells('A1:C1');
        sheet.getCell('A1').value = headerCodes.eleqe; // ELEQExxxxx
        sheet.getCell('A1').font = { bold: true, size: 16 };
        sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('A1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('D1:F1');
        sheet.getCell('D1').value = headerCodes.elent; // ELENTxxxxx
        sheet.getCell('D1').font = { bold: true, size: 16 };
        sheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('D1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('G1:I1');
        sheet.getCell('G1').value = settings.installazione || 'AT0700ZM00'; // Campo Installazione
        sheet.getCell('G1').font = { bold: true, size: 16 };
        sheet.getCell('G1').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('G1').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getRow(1).height = 38.25;

        // Seconda riga header - ALTEZZA 20.25
        sheet.mergeCells('A2:F2');
        sheet.getCell('A2').value = headerCodes.elecb; // ELECBxxxxx (nome file)
        sheet.getCell('A2').font = { bold: true, size: 14 };
        sheet.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('A2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.mergeCells('G2:H2');
        sheet.getCell('G2').value = settings.schemaElettrico || 'SCE-15032488'; // Campo Schema Elettrico
        sheet.getCell('G2').font = { bold: true, size: 12 };
        sheet.getCell('G2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('G2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getCell('I2').value = `Rev. ${revisionFormatted}`; // Revisione dalle impostazioni
        sheet.getCell('I2').font = { bold: true, size: 12 };
        sheet.getCell('I2').alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell('I2').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };

        sheet.getRow(2).height = 20.25;

        let currentRow = 4;

        // ==================== SEZIONE COMPONENTI ====================
        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = t('exportSiglature.components');
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            t('exportSiglature.componentSign'),
            t('exportSiglature.flatTags'),
            allResults.sectionAbove6,
            { ext: 'EXT', qe: 'QE', sw: 503, tipo: '42092B' },
            t, language // Pass t and language if needed, but createSectionWithData needs refactoring to use t
        );

        // ==================== SEZIONE MORSETTI ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = t('exportSiglature.terminals');
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            t('exportSiglature.terminalSign'),
            t('exportSiglature.terminalTags'),
            allResults.morsetti,
            { ext: 'QE', qe: '', sw: 42, tipo: '41390N' },
            t
        );

        // ==================== SEZIONE INTERRUTTORI E SPIE ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = t('exportSiglature.switchesAndLights');
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            t('exportSiglature.switchSign'),
            t('exportSiglature.switchTags'),
            [], // Nessun dato automatico per ora
            { ext: 'EXT', qe: '', sw: 166, tipo: '47091N' },
            t,
            '[TARGHETTA]', '[SIGLA]'
        );

        // ==================== SEZIONE 2,5-6,0 MMQ ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = t('exportSiglature.cables25_60');
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            t('exportSiglature.cable25Sign'),
            t('exportSiglature.cable25Tags'),
            allResults.section25_6,
            { ext: 'QE', qe: '', sw: 122, tipo: '40290' },
            t
        );

        // ==================== SEZIONE 0,5-1,5 MMQ ====================

        sheet.mergeCells(`A${currentRow}:I${currentRow}`);
        sheet.getCell(`A${currentRow}`).value = t('exportSiglature.cables05_15');
        sheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
        sheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getCell(`A${currentRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
        currentRow++;

        currentRow = createSectionWithData(
            sheet,
            currentRow,
            t('exportSiglature.cable05Sign'),
            t('exportSiglature.cable05Tags'),
            allResults.section05_15,
            { ext: 'QE', qe: '', sw: 121, tipo: '40190' },
            t
        );

        // ==================== FORMATTAZIONE FINALE ====================

        sheet.columns = [
            { width: 18 }, { width: 12 }, { width: 8 }, { width: 16 }, { width: 8 },
            { width: 12 }, { width: 15 }, { width: 15 }, { width: 15 }
        ];

        // Applica bordi a tutte le celle
        const lastRow = currentRow;
        for (let row = 1; row <= lastRow; row++) {
            const riga = sheet.getRow(row);
            const hasContent = riga.values.some((val, idx) =>
                idx >= 1 && idx <= 9 && val !== null && val !== undefined && val !== ''
            );

            if (hasContent) {
                for (let col = 1; col <= 9; col++) {
                    const cell = riga.getCell(col);
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                    if (!cell.alignment) {
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'center',
                            wrapText: true
                        };
                    }
                }
            }
        }

        // Salva il file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fullFileName = getFullFileName(settings, 'xlsx', false, false, false).replace('.xlsx', '-siglature.xlsx');

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fullFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSuccessMessage(t('fileOperations.siglatureExportedSuccess', { name: fullFileName }));
        setTimeout(() => setSuccessMessage(''), 5000);

    } catch (error) {
        console.error('Errore durante l\'esportazione siglature:', error);
        setWarnings(prev => [
            ...prev,
            t('fileOperations.exportSiglatureError', { error: error.message || error.toString() })
        ]);
    }
};

// ==================== FUNZIONE HELPER PER CREARE SEZIONI ====================
const createSectionWithData = (sheet, startRow, titleCol1, titleCol9, data, config, t, placeholderCol1 = '[SIGLA]', placeholderCol4 = '[SIGLA]') => {
    let currentRow = startRow;

    const headerRow = currentRow;
    const subHeaderRow = currentRow + 1;

    // MERGE VERTICALI
    sheet.mergeCells(`A${headerRow}:C${headerRow}`);
    sheet.mergeCells(`D${headerRow}:D${subHeaderRow}`);
    sheet.mergeCells(`E${headerRow}:E${subHeaderRow}`);
    sheet.mergeCells(`F${headerRow}:F${subHeaderRow}`);

    // Headers
    sheet.getCell(`A${headerRow}`).value = titleCol1;
    sheet.getCell(`A${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`A${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`A${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`D${headerRow}`).value = t('exportSiglature.qeExtLegend');
    sheet.getCell(`D${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`D${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`D${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`E${headerRow}`).value = t('exportSiglature.sw');
    sheet.getCell(`E${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`E${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`E${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getCell(`F${headerRow}`).value = t('exportSiglature.type');
    sheet.getCell(`F${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`F${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`F${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    // Colonne G e H vuote
    ['G', 'H'].forEach(col => {
        sheet.getCell(`${col}${headerRow}`).value = '';
        sheet.getCell(`${col}${headerRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    sheet.getCell(`I${headerRow}`).value = titleCol9;
    sheet.getCell(`I${headerRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`I${headerRow}`).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    sheet.getCell(`I${headerRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
    };

    sheet.getRow(headerRow).height = 35;

    // Sottotitoli
    [t('exportSiglature.start'), t('exportSiglature.repetitions'), t('exportSiglature.end')].forEach((label, idx) => {
        const cell = sheet.getCell(subHeaderRow, idx + 1);
        cell.value = label;
        cell.font = { bold: true, size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    // Colonne G e H vuote nel subheader
    ['G', 'H'].forEach(col => {
        sheet.getCell(`${col}${subHeaderRow}`).value = '';
        sheet.getCell(`${col}${subHeaderRow}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD3D3D3' }
        };
    });

    // CALCOLA E INSERISCI TOTALE
    const totalQuantity = data.length > 0
        ? data.reduce((sum, item) => sum + item.quantita, 0)
        : 1;

    sheet.getCell(`I${subHeaderRow}`).value = totalQuantity;
    sheet.getCell(`I${subHeaderRow}`).font = { bold: true, size: 10 };
    sheet.getCell(`I${subHeaderRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
    sheet.getCell(`I${subHeaderRow}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFF00' } // GIALLO per totale
    };

    currentRow = subHeaderRow + 1;

    // INSERISCI DATI REALI
    if (data.length > 0) {
        data.forEach(item => {
            const row = sheet.getRow(currentRow);
            row.values = [
                item.inizio,
                item.ripetizioni,
                item.fine || '',
                config.ext,
                config.sw,
                config.tipo,
                '',
                '',
                item.quantita
            ];
            row.eachCell((cell, colNumber) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                if (colNumber === 9) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFD3D3D3' }
                    };
                }
            });
            currentRow++;
        });
    } else {
        // Riga placeholder se non ci sono dati
        const row = sheet.getRow(currentRow);
        row.values = [placeholderCol1, 1, '', config.ext, config.sw, config.tipo, '', '', 1];
        row.eachCell((cell, colNumber) => {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: colNumber === 9 ? { argb: 'FFD3D3D3' } : { argb: 'FFF0F0F0' }
            };
        });
        currentRow++;
    }

    return currentRow + 1;
};

// Funzione helper per generare i codici dell'header
const generateHeaderCodes = (fileName) => {
    if (!fileName) {
        return {
            eleqe: 'ELEQExxxxx',
            elent: 'ELENTxxxxx',
            elecb: 'ELECBxxxxx'
        };
    }

    // Estrai la parte numerica
    let numericPart = '';

    if (fileName.match(/^ELECB\d{5}$/i)) {
        // Se è nel formato ELECBxxxxx, estrai le 5 cifre
        numericPart = fileName.slice(-5);
    } else if (fileName.match(/\d{5}/)) {
        // Se contiene 5 cifre consecutive, usale
        const match = fileName.match(/\d{5}/);
        numericPart = match[0];
    } else {
        // Fallback
        numericPart = 'xxxxx';
    }

        return {
            eleqe: `ELEQE${numericPart}`,
            elent: `ELENT${numericPart}`,
            elecb: fileName
        };
};

// Funzione per caricare le librerie PDF
const loadPDFLibraries = async () => {
    try {
        if (window.jspdf && window.svg2pdf) {
            return true;
        }

        // Carica jsPDF se non è già caricato
        if (!window.jspdf) {
            const jsPDFScript = document.createElement('script');
            jsPDFScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
            jsPDFScript.async = true;

            await new Promise((resolve, reject) => {
                jsPDFScript.onload = () => resolve();
                jsPDFScript.onerror = () => reject(new Error('Errore caricamento jsPDF'));
                document.head.appendChild(jsPDFScript);
            });
        }

        // Carica svg2pdf.js se non è già caricato
        if (!window.svg2pdf) {
            const svg2pdfScript = document.createElement('script');
            svg2pdfScript.src = 'https://unpkg.com/svg2pdf.js@2.2.1/dist/svg2pdf.umd.min.js';
            svg2pdfScript.async = true;

            await new Promise((resolve, reject) => {
                svg2pdfScript.onload = resolve;
                svg2pdfScript.onerror = reject;
                document.head.appendChild(svg2pdfScript);
            });
        }

        return true;
    } catch (error) {
        console.error('Errore nel caricamento delle librerie PDF:', error);
        return false;
    }
};

const exportSchematicToPDFBase = async ({
    cables,
    settings,
    setWarnings,
    setSuccessMessage,
    availableSheaths = [],
    setDynamicGroupsDivision = null,
    debugMode = false,
    groupSelector
}) => {
    const language = settings?.lingua || 'it';
    const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
            if (text && typeof text === 'object' && k in text) {
                text = text[k];
            } else {
                return key;
            }
        }
        if (typeof text === 'string' && params) {
            return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
    };

    const fileNameValidation = validateWindowsFileName(settings.fileName);
    if (settings.fileName && !fileNameValidation.isValid) {
        setWarnings(prev => [
            ...prev,
            t('fileOperations.invalidWindowsFileNameWarning')
        ]);
        return;
    }

    const originalDynamicGroupsDivision = settings.dynamicGroupsDivision;
    const cableIndexMap = new Map();
    cables.forEach((cable, index) => {
        if (cable.originalIndex !== undefined) {
            cableIndexMap.set(cable.originalIndex, cable);
        }
    });

    settings.dynamicGroupsDivision = true;
    settings.debugMode = debugMode;

    if (setDynamicGroupsDivision) {
        setDynamicGroupsDivision(true);
    }

    await new Promise(resolve => setTimeout(resolve, 100));

    try {
        const librariesLoaded = await loadPDFLibraries();
        if (!librariesLoaded) {
            setWarnings(prev => [...prev, t('fileOperations.pdfLibrariesError')]);
            return;
        }


        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 15;
        const usableWidth = pageWidth - (2 * margin);
        let currentY = margin;

        // Colori del tema
        const primaryColor = [59, 130, 246]; // Blue-600
        const secondaryColor = [99, 102, 241]; // Indigo-600
        const grayColor = [107, 114, 128]; // Gray-500
        const lightGrayColor = [229, 231, 235]; // Gray-200

        // HEADER COLORATO
        pdf.setFillColor(...primaryColor);
        pdf.rect(0, 0, pageWidth, 35, 'F');

        // Inserimento Logo (A Sinistra)
        const logoSize = 22;
        const logoX = margin;
        const logoY = 6.5;

        const logoAsset = await resolvePdfLogoAsset(settings);
        const renderedLogoSize = calculateLogoRenderSize(
            logoAsset.width || logoSize,
            logoAsset.height || logoSize,
            logoSize,
            logoSize
        );
        const renderedLogoX = logoX + ((logoSize - renderedLogoSize.width) / 2);
        const renderedLogoY = logoY + ((logoSize - renderedLogoSize.height) / 2);

        if (logoAsset.kind === 'svg') {
            try {
                const logoParser = new DOMParser();
                const logoDoc = logoParser.parseFromString(logoAsset.content, 'image/svg+xml');
                const logoSvgElement = logoDoc.documentElement;
                const parserError = logoDoc.querySelector('parsererror');

                if (parserError || !logoSvgElement || logoSvgElement.nodeName.toLowerCase() !== 'svg') {
                    throw new Error('SVG logo non valido');
                }

                await pdf.svg(logoSvgElement, {
                    x: renderedLogoX,
                    y: renderedLogoY,
                    width: renderedLogoSize.width,
                    height: renderedLogoSize.height
                });
            } catch (error) {
                const rasterizedLogo = await rasterizeSvgToPngDataUrl(
                    logoAsset.content,
                    logoAsset.width || 256,
                    logoAsset.height || 256
                );
                pdf.addImage(
                    rasterizedLogo,
                    'PNG',
                    renderedLogoX,
                    renderedLogoY,
                    renderedLogoSize.width,
                    renderedLogoSize.height
                );
            }
        } else {
            pdf.addImage(
                logoAsset.content,
                getPdfImageFormat(logoAsset.mimeType),
                renderedLogoX,
                renderedLogoY,
                renderedLogoSize.width,
                renderedLogoSize.height
            );
        }

        // Genera nome file se non fornito
        const fileName = settings.fileName || 'Progetto senza nome';

        // Calcola X per i testi (dopo il logo)
        const textStartX = margin + logoSize + 8;

        // Titolo principale (Spostato a destra del logo)
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(255, 255, 255);
        pdf.text(t('exportSchematic.title'), textStartX, 15);

        // Info riga 1 (Nome progetto + Revisione e ora anche Quantità Cablaggi)
        const infoY = 24;
        const revisionFormatted = settings.revision ? settings.revision.toString().padStart(2, '0') : '00';

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${fileName} - Rev. ${revisionFormatted}`, textStartX, infoY);

        const harnessQtyX = textStartX + (usableWidth - (textStartX - margin)) * 0.5;
        pdf.text(`${t('quantities.harnessQuantity') || 'Quantità cablaggi'}: ${settings.quantitaCablaggi || 1}`, harnessQtyX, infoY);

        const hasOrderReference = settings.commessaId && String(settings.commessaId).trim() !== '';
        const hasOrderDate = settings.commessaDate && String(settings.commessaDate).trim() !== '';

        if (hasOrderReference || hasOrderDate) {
            const orderInfoY = 30;
            pdf.setFontSize(10);

            if (hasOrderReference) {
                pdf.text(`${t('exportSchematic.orderReference')}: ${settings.commessaId}`, textStartX, orderInfoY);
            }

            if (hasOrderDate) {
                pdf.text(`${t('exportSchematic.orderDate')}: ${settings.commessaDate}`, harnessQtyX, orderInfoY);
            }
        }

        currentY = hasOrderReference || hasOrderDate ? 46 : 40; // Spazio dopo header

        // Helper per disegnare il layout di base della pagina
        const drawPageHeader = () => {
            pdf.setFillColor(...primaryColor);
            pdf.rect(0, 0, pageWidth, 12, 'F');
            pdf.setFontSize(9);
            pdf.setTextColor(255, 255, 255);

            const headerTitle = `${t('exportSchematic.pageHeader')}${fileName || t('exportSchematic.noName')}`;
            pdf.text(headerTitle, margin, 8);

            pdf.text(`${t('exportSchematic.page')} ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, 8);

            pdf.setTextColor(0, 0, 0);
            currentY = 20;
        };

        // ==================== RIEPILOGO QUANTITÀ CAVI ====================
        const quantities = calculateCableQuantities(cables, settings);

        // Helper conversione HEX -> RGB
        const hexToRgb = (hex) => {
            if (!hex) return null;
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? [
                parseInt(result[1], 16),
                parseInt(result[2], 16),
                parseInt(result[3], 16)
            ] : null;
        };

        const numHarnesses = settings.quantitaCablaggi || 1;
        const isMultiHarness = numHarnesses > 1;

        if (quantities.length > 0) {
            // Raggruppa per sezione
            const groupedBySection = {};
            const multipolarCables = [];
            let grandTotalCount = 0;
            let grandTotalLength = 0;

            quantities.forEach(item => {
                const itemTotalCount = item.count * numHarnesses;
                const itemTotalLength = item.totalLength * numHarnesses;

                grandTotalCount += itemTotalCount;
                grandTotalLength += itemTotalLength;

                if (item.isMultipolar) {
                    multipolarCables.push(item);
                } else {
                    const sectionKey = item.sezione;
                    if (!groupedBySection[sectionKey]) {
                        groupedBySection[sectionKey] = [];
                    }
                    groupedBySection[sectionKey].push(item);
                }
            });

            // Header sezione principale
            pdf.setFillColor(...secondaryColor);
            pdf.roundedRect(margin, currentY, usableWidth, 8, 2, 2, 'F');

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(t('exportSchematic.cableQuantities'), margin + 5, currentY + 5.5);

            currentY += 8;

            // Header Tabella
            const hasCad = isMultiHarness;
            const col1Width = hasCad ? usableWidth * 0.30 : usableWidth * 0.4;
            const col2Width = hasCad ? usableWidth * 0.12 : 0; // Quantità cad.
            const col3Width = hasCad ? usableWidth * 0.13 : usableWidth * 0.3; // Quantità tot.
            const col4Width = hasCad ? usableWidth * 0.225 : 0; // Lunghezza cad.
            const col1X = margin;
            const col2X = margin + col1Width;
            const col3X = margin + col1Width + col2Width;
            const col4X = margin + col1Width + col2Width + col3Width;
            const col5X = margin + col1Width + col2Width + col3Width + col4Width;

            pdf.setFillColor(...lightGrayColor);
            pdf.rect(margin, currentY, usableWidth, 6, 'F');

            pdf.setFontSize(9);
            pdf.setTextColor(...grayColor);
            pdf.setFont('helvetica', 'bold');

            pdf.text(t('exportSchematic.cableType'), col1X + 2, currentY + 4);
            if (hasCad) {
                pdf.text(t('exportSchematic.quantityCad') || "Quantità cad.", col2X + 2, currentY + 4);
                pdf.text(t('exportSchematic.quantityTot') || "Quantità tot.", col3X + 2, currentY + 4);
                pdf.text(t('exportSchematic.lengthCad') || "Lunghezza cad.", col4X + 2, currentY + 4);
                pdf.text(t('exportSchematic.lengthTot') || "Lunghezza tot.", col5X + 2, currentY + 4);
            } else {
                pdf.text(t('exportSchematic.quantity'), col3X + 2, currentY + 4);
                pdf.text(t('exportSchematic.totalLength'), col5X + 2, currentY + 4);
            }

            currentY += 6;

            // Funzione helper per disegnare una riga
            const drawRow = (col1Text, col2Text, col3Text, col4Text, col5Text, isHeader = false, isSubItem = false, colorName = null, isGrandTotal = false, itemIcon = null) => {
                pdf.setFontSize(9);
                let textIndent = isSubItem ? ((colorName || itemIcon) ? 12 : 10) : 2;
                const col1MaxWidth = Math.max(12, col1Width - textIndent - 3);
                const allCol1Lines = pdf.splitTextToSize(String(col1Text || ''), col1MaxWidth);
                const col1Lines = allCol1Lines.slice(0, 2);
                if (allCol1Lines.length > 2 && col1Lines.length === 2) {
                    let finalLine = `${col1Lines[1]}...`;
                    while (finalLine.length > 3 && pdf.getTextWidth(finalLine) > col1MaxWidth) {
                        finalLine = `${finalLine.slice(0, -4).trimEnd()}...`;
                    }
                    col1Lines[1] = finalLine;
                }
                const rowHeight = col1Lines.length > 1 ? 9 : 6;
                // Sfondo
                if (isHeader) {
                    pdf.setFillColor(224, 231, 255); // indigo-100 per header sezione
                    pdf.rect(margin, currentY, usableWidth, rowHeight, 'F');
                    pdf.setFont('helvetica', 'bold');
                } else if (isGrandTotal) {
                    pdf.setFillColor(200, 200, 200); // Grigio scuro per totale
                    pdf.rect(margin, currentY, usableWidth, rowHeight, 'F');
                    pdf.setFont('helvetica', 'bold');
                } else {
                    pdf.setFillColor(255, 255, 255);
                    pdf.setFont('helvetica', 'normal');
                }

                pdf.setTextColor(0, 0, 0);

                // Bordi verticali
                pdf.setDrawColor(229, 231, 235);
                pdf.line(col1X, currentY, col1X, currentY + rowHeight);
                if (hasCad) {
                    pdf.line(col2X, currentY, col2X, currentY + rowHeight);
                    pdf.line(col3X, currentY, col3X, currentY + rowHeight);
                    pdf.line(col4X, currentY, col4X, currentY + rowHeight);
                    pdf.line(col5X, currentY, col5X, currentY + rowHeight);
                } else {
                    pdf.line(col3X, currentY, col3X, currentY + rowHeight);
                    pdf.line(col5X, currentY, col5X, currentY + rowHeight);
                }
                pdf.line(margin + usableWidth, currentY, margin + usableWidth, currentY + rowHeight);

                // Indicatore Colore
                if (colorName && isSubItem) {
                    const rectSize = 4;
                    const rectY = currentY + ((rowHeight - rectSize) / 2);
                    const rectX = col1X + 4; // Spazio per indentazione
                    textIndent = 12; // Spazio extra per testo

                    // Gestione GI/VE (Giallo/Verde)
                    const swatchSegments = getColorSwatchSegments(colorName);
                    pdf.saveGraphicsState();
                    pdf.roundedRect(rectX, rectY, rectSize, rectSize, 0.6, 0.6, null);
                    pdf.clip();
                    pdf.discardPath();
                    swatchSegments.forEach((segmentHex, index) => {
                        const rgb = hexToRgb(segmentHex);
                        if (rgb) {
                            pdf.setFillColor(...rgb);
                            const segmentHeight = rectSize / swatchSegments.length;
                            pdf.rect(rectX, rectY + (index * segmentHeight), rectSize, segmentHeight, 'F');
                        }
                    });
                    pdf.restoreGraphicsState();

                    // Bordo per colori chiari o pattern multicolore
                    if (normalizeColorToCanonical(colorName) === 'WHITE' || swatchSegments.length > 1) {
                        pdf.setDrawColor(200, 200, 200);
                        pdf.roundedRect(rectX, rectY, rectSize, rectSize, 0.6, 0.6, 'S');
                    }
                }

                if (itemIcon === 'multipolar' && isSubItem) {
                    const rectSize = 4;
                    const rectX = col1X + 4;
                    const rectY = currentY + ((rowHeight - rectSize) / 2);
                    pdf.setFillColor(168, 85, 247);
                    pdf.roundedRect(rectX, rectY, rectSize, rectSize, 0.6, 0.6, 'F');
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(7.5);
                    pdf.setTextColor(255, 255, 255);
                    pdf.text('M', rectX + (rectSize / 2), rectY + (rectSize / 2), {
                        align: 'center',
                        baseline: 'middle'
                    });
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(9);
                    pdf.setTextColor(0, 0, 0);
                }

                const textStartY = col1Lines.length > 1 ? currentY + 3.5 : currentY + 4;
                col1Lines.forEach((line, lineIndex) => pdf.text(line, col1X + textIndent, textStartY + (lineIndex * 3.5)));
                const valueY = currentY + (rowHeight / 2) + 1;
                if (hasCad) {
                    pdf.text(col2Text || "", col2X + 2, valueY);
                    pdf.text(col3Text || "", col3X + 2, valueY);
                    pdf.text(col4Text || "", col4X + 2, valueY);
                } else {
                    pdf.text(col3Text || "", col3X + 2, valueY);
                }
                pdf.text(col5Text || "", col5X + 2, valueY);

                // Bordo inferiore
                pdf.setDrawColor(229, 231, 235);
                pdf.line(margin, currentY + rowHeight, margin + usableWidth, currentY + rowHeight);

                currentY += rowHeight;

                // Gestione pagina
                if (currentY > pageHeight - margin) {
                    pdf.addPage();
                    drawPageHeader();
                }
            };

            // Ordina sezioni
            const sortedSections = Object.keys(groupedBySection).sort((a, b) => parseFloat(a) - parseFloat(b));

            sortedSections.forEach(section => {
                const sectionItems = groupedBySection[section];

                // Calcola totali per la sezione
                const totalQty = sectionItems.reduce((acc, item) => acc + item.count, 0);
                const totalLen = sectionItems.reduce((acc, item) => acc + item.totalLength, 0);
                const sectionLabel = `${formatSezione(parseFloat(section), settings.useAWG)}`;

                // Disegna header sezione
                drawRow(
                    sectionLabel,
                    hasCad ? totalQty.toString() : "",
                    (totalQty * numHarnesses).toString(),
                    hasCad ? totalLen.toLocaleString() + ' mm' : "",
                    (totalLen * numHarnesses).toLocaleString() + ' mm',
                    true
                );

                // Disegna item della sezione
                sectionItems.forEach(item => {
                    const colorName = item.colore;
                    const itemLabel = item.codiceInterno
                        ? `[${item.codiceInterno}]${item.descrizione ? ` ${item.descrizione}` : ''}`
                        : translateColorForDisplay(colorName, language);
                    drawRow(
                        itemLabel,
                        hasCad ? item.count.toString() : "",
                        (item.count * numHarnesses).toString(),
                        hasCad ? item.totalLength.toLocaleString() + ' mm' : "",
                        (item.totalLength * numHarnesses).toLocaleString() + ' mm',
                        false, true, colorName
                    );
                });
            });

            // Cavi multipolari
            if (multipolarCables.length > 0) {
                const totalQty = multipolarCables.reduce((acc, item) => acc + item.count, 0);
                const totalLen = multipolarCables.reduce((acc, item) => acc + item.totalLength, 0);

                drawRow(
                    t('exportSchematic.multipolar'),
                    hasCad ? totalQty.toString() : "",
                    (totalQty * numHarnesses).toString(),
                    hasCad ? totalLen.toLocaleString() + ' mm' : "",
                    (totalLen * numHarnesses).toLocaleString() + ' mm',
                    true
                );

                multipolarCables.forEach(item => {
                    const itemLabel = item.codiceInterno
                        ? `[${item.codiceInterno}]${item.descrizione ? ` ${item.descrizione}` : ''} ${item.colore}`
                        : `${translateColorForDisplay(item.colore, language)}`;
                    drawRow(
                        itemLabel,
                        hasCad ? item.count.toString() : "",
                        (item.count * numHarnesses).toString(),
                        hasCad ? item.totalLength.toLocaleString() + ' mm' : "",
                        (item.totalLength * numHarnesses).toLocaleString() + ' mm',
                        false, true, null, false, 'multipolar'
                    );
                });
            }

            // RIGA TOTALE
            drawRow(
                t('exportSchematic.total'),
                hasCad ? (grandTotalCount / numHarnesses).toString() : "",
                grandTotalCount.toString(),
                hasCad ? (grandTotalLength / numHarnesses).toLocaleString() + ' mm' : "",
                grandTotalLength.toLocaleString() + ' mm',
                false, false, null, true
            );

            // Bordo finale tabella più scuro
            pdf.setDrawColor(209, 213, 219);
            pdf.line(margin, currentY, margin + usableWidth, currentY);

            currentY += 5;
        }

        // ==================== RIEPILOGO TERMINALI ====================
        let terminalQuantities = calculateTerminalQuantities(cables, settings, {});

        // Filtra terminali fittizi SOLO per il PDF (NS = No Spelatura, SP... = Spelature Personalizzate)
        terminalQuantities = terminalQuantities.filter(t => !t.terminal.startsWith('SP') && t.terminal !== 'NS');

        if (terminalQuantities.length > 0) {
            // Check page space
            if (currentY > pageHeight - margin) {
                pdf.addPage();
                drawPageHeader();
            }

            // Header Sezione
            pdf.setFillColor(...secondaryColor);
            pdf.roundedRect(margin, currentY, usableWidth, 8, 2, 2, 'F');

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(t('exportSchematic.terminalQuantities'), margin + 5, currentY + 5.5);

            currentY += 8;

            // Header Tabella
            const hasCad = isMultiHarness;
            const col1Width = hasCad ? usableWidth * 0.55 : usableWidth * 0.7;
            const col2Width = hasCad ? usableWidth * 0.22 : 0; // Quantità cad.

            const col1X = margin;
            const col2X = margin + col1Width;
            const col3X = margin + col1Width + col2Width;

            pdf.setFillColor(...lightGrayColor);
            pdf.rect(margin, currentY, usableWidth, 6, 'F');

            pdf.setFontSize(9);
            pdf.setTextColor(...grayColor);
            pdf.setFont('helvetica', 'bold');

            pdf.text(t('exportSchematic.terminal'), col1X + 2, currentY + 4);
            if (hasCad) {
                pdf.text(t('exportSchematic.quantityCad') || "Quantità cad.", col2X + 2, currentY + 4);
            }
            pdf.text(hasCad ? (t('exportSchematic.quantityTot') || "Quantità tot.") : t('exportSchematic.quantity'), col3X + 2, currentY + 4);

            currentY += 6;

            // Righe
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);

            let totalTerminals = 0;

            terminalQuantities.forEach((item, index) => {
                const itemTotalCount = item.count * numHarnesses;
                totalTerminals += itemTotalCount;

                if (index % 2 === 0) {
                    pdf.setFillColor(248, 250, 252);
                    pdf.rect(margin, currentY, usableWidth, 6, 'F');
                }

                pdf.setDrawColor(229, 231, 235);
                pdf.line(col1X, currentY, col1X, currentY + 6);
                if (hasCad) pdf.line(col2X, currentY, col2X, currentY + 6);
                pdf.line(col3X, currentY, col3X, currentY + 6);
                pdf.line(margin + usableWidth, currentY, margin + usableWidth, currentY + 6);

                // Recupera nome completo se disponibile
                const terminalName = item.terminalData ? item.terminalData.nome : item.terminal;
                const terminalLabel = `${item.terminal} - ${terminalName}`;

                pdf.text(terminalLabel, col1X + 2, currentY + 4);
                if (hasCad) {
                    pdf.text(item.count.toString(), col2X + 2, currentY + 4);
                }
                pdf.text(itemTotalCount.toString(), col3X + 2, currentY + 4);

                currentY += 6;

                if (currentY > pageHeight - margin) {
                    pdf.addPage();
                    drawPageHeader();
                }
            });

            // Totale Terminali
            pdf.setFillColor(200, 200, 200);
            pdf.rect(margin, currentY, usableWidth, 6, 'F');
            pdf.setFont('helvetica', 'bold');
            pdf.text(t('exportSchematic.total'), col1X + 2, currentY + 4);
            if (hasCad) {
                pdf.text((totalTerminals / numHarnesses).toString(), col2X + 2, currentY + 4);
            }
            pdf.text(totalTerminals.toString(), col3X + 2, currentY + 4);

            // Bordo inferiore
            pdf.setDrawColor(209, 213, 219);
            pdf.line(margin, currentY + 6, margin + usableWidth, currentY + 6); // Linea sotto totale

            pdf.setDrawColor(229, 231, 235);
            pdf.line(col1X, currentY, col1X, currentY + 6);
            if (hasCad) {
                pdf.line(col2X, currentY, col2X, currentY + 6);
                pdf.line(col3X, currentY, col3X, currentY + 6);
            } else {
                pdf.line(col3X, currentY, col3X, currentY + 6);
            }
            pdf.line(margin + usableWidth, currentY, margin + usableWidth, currentY + 6);

            // Bordo finale
            pdf.setDrawColor(209, 213, 219);
            pdf.line(margin, currentY + 6, margin + usableWidth, currentY + 6);

            currentY += 11;
        }

        // ==================== RIEPILOGO GUAINE ====================
        const sheathQuantities = calculateSheathQuantities(cables, availableSheaths);

        if (sheathQuantities.length > 0) {
            // Check page space
            if (currentY > pageHeight - margin) {
                pdf.addPage();
                drawPageHeader();
            }

            // Header Sezione
            pdf.setFillColor(...secondaryColor);
            pdf.roundedRect(margin, currentY, usableWidth, 8, 2, 2, 'F');

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(t('exportSchematic.sheathQuantities'), margin + 5, currentY + 5.5);

            currentY += 8;

            // Header Tabella
            const hasCad = isMultiHarness;
            const col1Width = hasCad ? usableWidth * 0.30 : usableWidth * 0.6;
            const col2Width = hasCad ? usableWidth * 0.12 : 0; // Quantità cad.
            const col3Width = hasCad ? usableWidth * 0.13 : usableWidth * 0.2; // Quantità tot.
            const col4Width = hasCad ? usableWidth * 0.225 : 0; // Lunghezza cad.
            const col1X = margin;
            const col2X = margin + col1Width;
            const col3X = margin + col1Width + col2Width;
            const col4X = margin + col1Width + col2Width + col3Width;
            const col5X = margin + col1Width + col2Width + col3Width + col4Width;

            pdf.setFillColor(...lightGrayColor);
            pdf.rect(margin, currentY, usableWidth, 6, 'F');

            pdf.setFontSize(9);
            pdf.setTextColor(...grayColor);
            pdf.setFont('helvetica', 'bold');

            pdf.text(t('exportSchematic.sheath'), col1X + 2, currentY + 4);
            if (hasCad) {
                pdf.text(t('exportSchematic.quantityCad') || "Quantità cad.", col2X + 2, currentY + 4);
                pdf.text(t('exportSchematic.quantityTot') || "Quantità tot.", col3X + 2, currentY + 4);
                pdf.text(t('exportSchematic.lengthCad') || "Lunghezza cad.", col4X + 2, currentY + 4);
                pdf.text(t('exportSchematic.lengthTot') || "Lunghezza tot.", col5X + 2, currentY + 4);
            } else {
                pdf.text(t('exportSchematic.quantity'), col3X + 2, currentY + 4);
                pdf.text(t('exportSchematic.totalLength'), col5X + 2, currentY + 4);
            }

            currentY += 6;

            // Righe Tabella
            pdf.setDrawColor(229, 231, 235);
            pdf.setFontSize(9);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(0, 0, 0);

            let totalSheathCount = 0;
            let totalSheathLength = 0;

            sheathQuantities.forEach((item, index) => {
                if (currentY > pageHeight - margin) {
                    pdf.addPage();
                    drawPageHeader();
                }

                const itemTotalCount = item.count * numHarnesses;
                const itemTotalLength = item.totalLength * numHarnesses;

                totalSheathCount += itemTotalCount;
                totalSheathLength += itemTotalLength;

                if (index % 2 === 0) {
                    pdf.setFillColor(248, 250, 252);
                    pdf.rect(margin, currentY, usableWidth, 6, 'F');
                }

                pdf.setDrawColor(229, 231, 235);
                pdf.line(col1X, currentY, col1X, currentY + 6);
                if (hasCad) {
                    pdf.line(col2X, currentY, col2X, currentY + 6);
                    pdf.line(col3X, currentY, col3X, currentY + 6);
                    pdf.line(col4X, currentY, col4X, currentY + 6);
                    pdf.line(col5X, currentY, col5X, currentY + 6);
                } else {
                    pdf.line(col3X, currentY, col3X, currentY + 6);
                    pdf.line(col5X, currentY, col5X, currentY + 6);
                }
                pdf.line(margin + usableWidth, currentY, margin + usableWidth, currentY + 6);

                // Disegna label
                const label = item.type + (item.label ? ` - ${item.label}` : '') + (item.diameter ? ` (Ø${item.diameter})` : '');

                pdf.text(label, col1X + 2, currentY + 4);
                if (hasCad) {
                    pdf.text(item.count.toString(), col2X + 2, currentY + 4);
                    pdf.text(itemTotalCount.toString(), col3X + 2, currentY + 4);
                    pdf.text(item.totalLength + ' mm', col4X + 2, currentY + 4);
                } else {
                    pdf.text(itemTotalCount.toString(), col3X + 2, currentY + 4);
                }
                pdf.text(itemTotalLength + ' mm', col5X + 2, currentY + 4);

                currentY += 6;
            });

            // Riga Totale Guaine
            pdf.setFillColor(200, 200, 200);
            pdf.rect(margin, currentY, usableWidth, 6, 'F');
            pdf.setFont('helvetica', 'bold');

            pdf.text(t('exportSchematic.total'), col1X + 2, currentY + 4);
            if (hasCad) {
                pdf.text((totalSheathCount / numHarnesses).toString(), col2X + 2, currentY + 4);
                pdf.text(totalSheathCount.toString(), col3X + 2, currentY + 4);
                pdf.text((totalSheathLength / numHarnesses) + ' mm', col4X + 2, currentY + 4);
            } else {
                pdf.text(totalSheathCount.toString(), col3X + 2, currentY + 4);
            }
            pdf.text(totalSheathLength + ' mm', col5X + 2, currentY + 4);

            // Bordo inferiore e verticali totale
            pdf.setDrawColor(229, 231, 235);
            pdf.line(col1X, currentY, col1X, currentY + 6);
            if (hasCad) {
                pdf.line(col2X, currentY, col2X, currentY + 6);
                pdf.line(col3X, currentY, col3X, currentY + 6);
                pdf.line(col4X, currentY, col4X, currentY + 6);
                pdf.line(col5X, currentY, col5X, currentY + 6);
            } else {
                pdf.line(col3X, currentY, col3X, currentY + 6);
                pdf.line(col5X, currentY, col5X, currentY + 6);
            }
            pdf.line(margin + usableWidth, currentY, margin + usableWidth, currentY + 6);

            // Bordo finale
            pdf.setDrawColor(209, 213, 219);
            pdf.line(margin, currentY + 6, margin + usableWidth, currentY + 6);

            currentY += 11;
        }

        // Forza una nuova pagina dopo i riepiloghi
        pdf.addPage();
        drawPageHeader();

        const elementsToHide = [];
        document.querySelectorAll('.p-2.text-blue-600, .p-2.text-red-600').forEach(el => {
            elementsToHide.push({ element: el, display: el.style.display });
            el.style.display = 'none';
        });

        const cableGroups = document.querySelectorAll(groupSelector);

        for (let i = 0; i < cableGroups.length; i++) {
            const group = cableGroups[i];
            const titleElement = group.querySelector('h3.text-lg.font-semibold.text-gray-800');
            const actualTitle = titleElement ? titleElement.textContent.trim() : `Gruppo ${i + 1}`;

            // Ottieni il codice del gruppo dai cavi
            let groupCode = '';
            const firstCableElement = group.querySelector('div[class*="border-2"][class*="rounded-lg"] span');
            const datasetGroupCode = group.dataset?.groupCode;
            if (datasetGroupCode) {
                groupCode = datasetGroupCode;
            } else if (firstCableElement) {
                const cableIndex = parseInt(firstCableElement.textContent.replace('#', '')) - 1;
                if (cableIndex >= 0 && cableIndexMap.has(cableIndex)) {
                    const cableData = cableIndexMap.get(cableIndex);
                    if (cableData && cableData.gruppo) {
                        groupCode = cableData.gruppo;
                    }
                }
            }

            // ✅ MIGLIORATO: Calcolo preciso delle altezze usando il modulo centralizzato
            const detailsSection = group.querySelector('.bg-blue-50.rounded-lg.p-6');
            const cableDetails = detailsSection ? detailsSection.querySelectorAll('.space-y-2 > div') : [];

            // Calcola altezza SVG dal DOM
            const svgs = group.querySelectorAll('.bg-gray-50.rounded-lg.p-4 svg, .bg-gray-50.rounded-lg.p-2 svg');
            let groupHeight = PDF_CONSTANTS.HEADER_HEIGHT;

            let totalSvgHeight = 0;
            for (let j = 0; j < svgs.length; j++) {
                totalSvgHeight += readSVGHeightFromDOM(svgs[j], usableWidth);
            }

            groupHeight += totalSvgHeight;

            // Calcolo dettagliato altezza tabella usando il modulo centralizzato
            let detailsHeight = 0;
            if (cableDetails.length > 0) {
                detailsHeight = PDF_CONSTANTS.DETAILS_TABLE_HEADER;

                cableDetails.forEach((cableDetailDiv) => {
                    const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');
                    if (tessera) {
                        const cableIndex = parseInt(tessera.querySelector('span')?.textContent?.replace('#', '') - 1);
                        const cableData = cableIndexMap.get(cableIndex);
                        detailsHeight += estimateCableDetailHeight(cableData);
                    }
                });
            }

            groupHeight += detailsHeight;

            // ✅ MIGLIORATO: Gestione paginazione intelligente
            const maxGroupHeightPerPage = PDF_CONSTANTS.MAX_GROUP_HEIGHT_OTHER_PAGES;

            // Se il gruppo non entra nella pagina corrente, vai alla prossima
            // ✅ FIX: Non creare pagine vuote - se la pagina è fresca (solo header), renderizza qui
            if (groupHeight <= maxGroupHeightPerPage && currentY + groupHeight > pageHeight - margin && currentY > 30) {
                pdf.addPage();

                // Header semplificato per pagine successive
                drawPageHeader();
            }

            // ✅ NUOVO: Se il gruppo è ancora troppo alto, dividilo
            if (groupHeight > maxGroupHeightPerPage) {
                // Renderizza solo SVG nella pagina corrente
                const svgOnlyHeight = 12 + totalSvgHeight;

                // Se nemmeno il blocco iniziale del gruppo entra nello spazio residuo,
                // spostalo direttamente alla pagina successiva per evitare box troncati.
                if (currentY + svgOnlyHeight > pageHeight - margin && currentY > 30) {
                    pdf.addPage();
                    drawPageHeader();
                }

                // Riquadro principale per SVG
                pdf.setFillColor(255, 255, 255);
                pdf.setDrawColor(...lightGrayColor);
                pdf.setLineWidth(0.5);
                pdf.roundedRect(margin, currentY, usableWidth, svgOnlyHeight, 4, 4, 'FD');

                // Header colorato
                pdf.setFillColor(...secondaryColor);
                pdf.roundedRect(margin, currentY, usableWidth, 10, 4, 4, 'F');
                pdf.rect(margin, currentY + 4, usableWidth, 7, 'F');

                pdf.setFontSize(13);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(255, 255, 255);
                pdf.text(actualTitle, margin + 5, currentY + 7);

                // Aggiungi il codice del gruppo sulla destra se presente
                if (groupCode) {
                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(255, 255, 255);
                    const groupCodeText = groupCode;
                    const groupCodeWidth = pdf.getTextWidth(groupCodeText);
                    const groupCodeX = margin + usableWidth - groupCodeWidth - 10;
                    const groupCodeY = currentY + 5.5;

                    // Sfondo colorato vivace per il codice del gruppo
                    pdf.setFillColor(10, 186, 181); // #0abab5
                    pdf.roundedRect(groupCodeX - 3, groupCodeY - 2.9, groupCodeWidth + 6, 5, 2, 2, 'F');

                    // Testo del codice
                    pdf.text(groupCodeText, groupCodeX, groupCodeY + 1);
                }

                let innerY = currentY + 13;

                // Renderizza SVG
                for (let j = 0; j < svgs.length; j++) {
                    const svg = svgs[j];
                    const svgClone = createCleanSVGForExport(svg);

                    const viewBox = svg.getAttribute('viewBox').split(' ');
                    const svgWidth = parseFloat(viewBox[2]);
                    const svgHeight = parseFloat(viewBox[3]);
                    const scale = (usableWidth - 6) / svgWidth;
                    const scaledHeight = svgHeight * scale;

                    await pdf.svg(svgClone, {
                        x: margin + 3,
                        y: innerY,
                        width: usableWidth - 6,
                        height: scaledHeight
                    });

                    innerY += scaledHeight + 3;
                }

                // Vai alla pagina successiva per i dettagli (se ci sono)
                if (cableDetails.length > 0) {
                    pdf.addPage();
                    currentY = margin;

                    // Header pagina dettagli
                    pdf.setFillColor(...primaryColor);
                    pdf.rect(0, 0, pageWidth, 12, 'F');
                    pdf.setFontSize(9);
                    pdf.setTextColor(255, 255, 255);
                    pdf.text(`Dettagli ${actualTitle}`, margin, 8);
                    pdf.text(`${t('exportSchematic.page')} ${pdf.internal.getNumberOfPages()}`, pageWidth - margin - 15, 8);
                    currentY = 20;

                    // Renderizza solo la tabella dettagli
                    currentY = await renderDetailedCableTable(pdf, cableDetails, currentY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap, t, language);
                    currentY += 8;
                } else {
                    currentY = innerY + 5;
                }

                // Il gruppo oversized ha occupato una o piu pagine dedicate:
                // forza il prossimo gruppo a partire su una nuova pagina,
                // evitando di sovrascrivere una pagina dettagli appena renderizzata.
                currentY = pageHeight;
            } else {
                // ✅ GRUPPO NORMALE: Rendering completo in una pagina
                // Riquadro principale
                pdf.setFillColor(255, 255, 255);
                pdf.setDrawColor(...lightGrayColor);
                pdf.setLineWidth(0.5);
                pdf.roundedRect(margin, currentY, usableWidth, groupHeight, 4, 4, 'FD');

                // Header colorato attaccato
                pdf.setFillColor(...secondaryColor);
                pdf.roundedRect(margin, currentY, usableWidth, 10, 4, 4, 'F');
                pdf.rect(margin, currentY + 4, usableWidth, 7, 'F');

                pdf.setFontSize(13);
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(255, 255, 255);
                pdf.text(actualTitle, margin + 5, currentY + 7);

                // Aggiungi il codice del gruppo sulla destra se presente
                if (groupCode) {
                    pdf.setFontSize(10);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(255, 255, 255);
                    const groupCodeText = groupCode;
                    const groupCodeWidth = pdf.getTextWidth(groupCodeText);
                    const groupCodeX = margin + usableWidth - groupCodeWidth - 10;
                    const groupCodeY = currentY + 5.5;

                    // Sfondo colorato vivace per il codice del gruppo
                    pdf.setFillColor(10, 186, 181); // #0abab5
                    pdf.roundedRect(groupCodeX - 3, groupCodeY - 2.9, groupCodeWidth + 6, 5, 2, 2, 'F');

                    // Testo del codice
                    pdf.text(groupCodeText, groupCodeX, groupCodeY + 1);
                }

                let innerY = currentY + 13;

                // SVG della visualizzazione
                for (let j = 0; j < svgs.length; j++) {
                    const svg = svgs[j];
                    const svgClone = createCleanSVGForExport(svg);

                    const viewBox = svg.getAttribute('viewBox').split(' ');
                    const svgWidth = parseFloat(viewBox[2]);
                    const svgHeight = parseFloat(viewBox[3]);
                    const scale = (usableWidth - 6) / svgWidth;
                    const scaledHeight = svgHeight * scale;

                    await pdf.svg(svgClone, {
                        x: margin + 3,
                        y: innerY,
                        width: usableWidth - 6,
                        height: scaledHeight
                    });

                    // 🔍 DEBUG: Rettangolo per ogni SVG individuale
                    if (settings.debugMode) {
                        pdf.setDrawColor(0, 255, 0); // Verde
                        pdf.setLineWidth(0.3);
                        pdf.rect(margin + 3, innerY, usableWidth - 6, scaledHeight);

                        // Testo con dimensioni SVG
                        pdf.setFontSize(6);
                        pdf.setTextColor(0, 128, 0);
                        pdf.text(`SVG ${j + 1}: ${svgHeight.toFixed(1)}px → ${scaledHeight.toFixed(1)}mm`, margin + 5, innerY + 4);
                    }

                    innerY += scaledHeight + 3;
                }

                if (cableDetails.length > 0) {
                    innerY = await renderDetailedCableTable(pdf, cableDetails, innerY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap, t, language);
                }

                // 🔍 DEBUG: Rettangoli per altezze totali del gruppo
                if (settings.debugMode) {
                    // Rettangolo per altezza SVG totale (ROSSO)
                    pdf.setDrawColor(255, 0, 0);
                    pdf.setLineWidth(0.5);
                    pdf.rect(margin + 2, currentY + 13, usableWidth - 4, totalSvgHeight);

                    // Testo con altezza SVG stimata
                    pdf.setFontSize(8);
                    pdf.setTextColor(255, 0, 0);
                    pdf.text(`SVG Total: ${totalSvgHeight.toFixed(1)}mm`, margin + 4, currentY + 17);

                    // Rettangolo per altezza totale del gruppo (BLU)
                    pdf.setDrawColor(0, 0, 255);
                    pdf.rect(margin + 1, currentY + 13, usableWidth - 2, groupHeight - 13);

                    // Testo con altezza totale
                    pdf.setTextColor(0, 0, 255);
                    pdf.text(`Group Total: ${groupHeight.toFixed(1)}mm`, margin + 4, currentY + 21);
                }

                currentY += groupHeight + 8;
            }
        }

        // FOOTER
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);

            pdf.setDrawColor(...lightGrayColor);
            pdf.setLineWidth(0.5);
            pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

            pdf.setFontSize(8);
            pdf.setTextColor(...grayColor);

            const dateStr = new Date().toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US');
            const timeStr = new Date().toLocaleTimeString(language === 'it' ? 'it-IT' : 'en-US');
            const footerText = `${t('exportSchematic.generatedWith')} - ${dateStr} ${timeStr}`;

            pdf.text(footerText, margin, pageHeight - 8);
            pdf.text(`${t('exportSchematic.page')} ${i} ${t('exportSchematic.of')} ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        }

        // Ripristina elementi nascosti
        elementsToHide.forEach(({ element, display }) => {
            if (display !== undefined) element.style.display = display;
        });

        // Salva PDF
        const pdfFileName = settings.fileName ?
            getFullFileName(settings, 'pdf', false, false).replace('.pdf', '-schema-colori.pdf') :
            'schema-cablaggi-colori.pdf';

        pdf.save(pdfFileName);
        setSuccessMessage(t('fileOperations.pdfExportedSuccess', { name: pdfFileName }));
        setTimeout(() => setSuccessMessage(''), 5000);

        settings.dynamicGroupsDivision = originalDynamicGroupsDivision;

        if (setDynamicGroupsDivision) {
            setDynamicGroupsDivision(originalDynamicGroupsDivision);
        }

    } catch (error) {
        console.error('Errore esportazione PDF:', error);
        setWarnings(prev => [...prev, t('fileOperations.exportPDFError', { error: error.message })]);

        settings.dynamicGroupsDivision = originalDynamicGroupsDivision;

        if (setDynamicGroupsDivision) {
            setDynamicGroupsDivision(originalDynamicGroupsDivision);
        }
    }
};

export const exportSchematicToPDFColor = async (cables, settings, setWarnings, setSuccessMessage, availableSheaths = [], setDynamicGroupsDivision = null, debugMode = false) => {
    settings = withEW3035Settings(settings);
    return exportSchematicToPDFBase({
        cables,
        settings,
        setWarnings,
        setSuccessMessage,
        availableSheaths,
        setDynamicGroupsDivision,
        debugMode,
        groupSelector: '[id^="cable-group-"]'
    });
};

export const exportSchematicNestedToPDF = async (cables, settings, setWarnings, setSuccessMessage, availableSheaths = [], setDynamicGroupsDivision = null, debugMode = false) => {
    settings = withEW3035Settings(settings);
    return exportSchematicToPDFBase({
        cables,
        settings,
        setWarnings,
        setSuccessMessage,
        availableSheaths,
        setDynamicGroupsDivision,
        debugMode,
        groupSelector: '[data-nested-node-id]'
    });
};

// Rendering tabella dettagliata
const renderDetailedCableTable = async (pdf, cableDetails, startY, margin, usableWidth, primaryColor, lightGrayColor, grayColor, cables, settings, cableIndexMap, t, language) => {
    let currentY = startY;

    const getCatalogLabel = (cableData) => {
        const code = String(cableData?.catalogAssignment?.codiceInterno || '').trim();
        if (!code) return '';
        const description = String(cableData?._catalogDescription || '').trim();
        return `[${code}]${description ? ` ${description}` : ''}`;
    };

    const fitTextToWidth = (text, maxWidth) => {
        const value = String(text || '');
        if (!value || maxWidth <= 0) return '';
        if (pdf.getTextWidth(value) <= maxWidth) return value;

        let fitted = value;
        while (fitted.length > 1 && pdf.getTextWidth(`${fitted.trimEnd()}...`) > maxWidth) {
            fitted = fitted.slice(0, -1);
        }
        return fitted.length > 1 ? `${fitted.trimEnd()}...` : '';
    };

    // Separatore elegante
    pdf.setDrawColor(...lightGrayColor);
    pdf.setLineWidth(0.3);
    pdf.line(margin + 10, currentY, margin + usableWidth - 10, currentY);
    currentY += 5;

    // Titolo sezione con sfondo colorato
    pdf.setFillColor(...primaryColor);
    pdf.roundedRect(margin + 3, currentY - 2, 60, 6, 2, 2, 'F');
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text(t('exportSchematic.technicalDetails'), margin + 6, currentY + 2);
    currentY += 8;

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');

    cableDetails.forEach((cableDetailDiv, idx) => {
        const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');

        if (tessera) {
            // Alternanza colori righe - più delicata
            if (idx % 2 === 0) {
                pdf.setFillColor(248, 250, 252); // bg-slate-50
                pdf.roundedRect(margin + 3, currentY - 2, usableWidth - 6, 0, 2, 2, 'F'); // Altezza dinamica
            }

            // Badge numero - più grande e colorato
            const numberBadge = tessera.querySelector('span[class*="px-2"][class*="py-0.5"]');
            const numberText = numberBadge ? numberBadge.textContent.trim() : '';

            if (numberText) {
                const cableIndex = parseInt(numberText.replace('#', '')) - 1;
                const cableData = cableIndexMap.get(cableIndex);

                if (cableData) {
                    // Badge numero migliorato
                    pdf.setFillColor(...primaryColor);
                    pdf.roundedRect(margin + 5, currentY - 1, 15, 6, 2, 2, 'F');
                    pdf.setTextColor(255, 255, 255);
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(9);
                    pdf.text(numberText, margin + 12.5, currentY + 3, { align: 'center' });

                    pdf.setTextColor(0, 0, 0);
                    pdf.setFont('helvetica', 'normal');

                    if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
                        // ✅ CAVO MULTIPOLARE - LAYOUT OTTIMIZZATO

                        // Titolo cavo
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(10);
                        let mainTitle = `${cableData.nomeCavo || t('exportSchematic.multipolarCable')}`;

                        // Aggiungi siglatura se presente
                        let siglaturaText = '';
                        if (!settings.marcaturePerCollegamento) {
                            siglaturaText = '';
                        } else {
                            try {
                                const siglature = new Set();
                                cableData.poli.forEach(polo => {
                                    try {
                                        const sigA = extractSiglaturaFromCollegamento(polo.collegamentoA);
                                        const sigB = extractSiglaturaFromCollegamento(polo.collegamentoB);
                                        if (sigA && String(sigA).trim() !== '') siglature.add(String(sigA).trim());
                                        if (sigB && String(sigB).trim() !== '') siglature.add(String(sigB).trim());
                                    } catch (e) {
                                        console.warn('Errore estrazione siglatura polo:', e);
                                    }
                                });
                                if (siglature.size > 0) {
                                    siglaturaText = ` - Sig: ${Array.from(siglature).join(', ')}`;
                                }
                            } catch (e) {
                                console.warn('Errore estrazione siglature multipolari:', e);
                                siglaturaText = '';
                            }
                        }

                        // Calcola larghezza reale del titolo per evitare sovrapposizioni
                        const fullTitleString = `${mainTitle}${siglaturaText}`;
                        const titleStartX = margin + 25;
                        pdf.text(fullTitleString, titleStartX, currentY + 3);

                        // Misura ingombro del titolo in grassetto
                        const titleWidth = pdf.getTextWidth(fullTitleString);

                        // ✅ RIGA INFO GENERALI
                        const sezioni = [...new Set(cableData.poli.map(p => p.sezione))];
                        const hasUniformSection = sezioni.length === 1;

                        const multipolarLabel = getMultipolarLabel(cableData.poli, settings.useAWG);
                        const infoGenerali = `${multipolarLabel} • ${cableData.lunghezza}mm`;

                        const rightEdge = margin + usableWidth - 5;
                        const catalogStartX = titleStartX + titleWidth + 6;
                        const catalogLabel = getCatalogLabel(cableData);

                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(8);
                        const infoWidth = pdf.getTextWidth(infoGenerali);
                        let catalogWidth = 0;

                        if (catalogLabel) {
                            pdf.setFont('helvetica', 'bold');
                            pdf.setTextColor(...primaryColor);
                            const fittedCatalogLabel = fitTextToWidth(catalogLabel, Math.max(12, rightEdge - catalogStartX - infoWidth - 8));
                            if (fittedCatalogLabel) {
                                pdf.text(fittedCatalogLabel, catalogStartX, currentY + 3);
                                catalogWidth = pdf.getTextWidth(fittedCatalogLabel) + 6;
                            }
                        }

                        pdf.setTextColor(0, 0, 0);
                        pdf.setFont('helvetica', 'normal');
                        const infoStartX = catalogStartX + catalogWidth;
                        const fittedInfo = fitTextToWidth(infoGenerali, rightEdge - infoStartX);
                        if (fittedInfo) pdf.text(fittedInfo, infoStartX, currentY + 3);
                        currentY += 8;

                        // ✅ HEADER TABELLA OTTIMIZZATO - Con o senza sezione
                        const headerY = currentY;
                        pdf.setFillColor(...lightGrayColor);
                        pdf.roundedRect(margin + 20, headerY - 1, usableWidth - 25, 6, 1, 1, 'F');

                        pdf.setFontSize(7);
                        pdf.setFont('helvetica', 'bold');
                        pdf.setTextColor(...grayColor);

                        // ✅ COLONNE OTTIMIZZATE - Con siglature invece della sezione se uniforme
                        let cols;
                        if (hasUniformSection) {
                            // Layout con siglature (senza sezione)
                            cols = {
                                polo: margin + 25,
                                colore: margin + 40,
                                sigA: margin + 55,
                                termA: margin + 70,
                                lunghA: margin + 85,
                                collA: margin + 100,
                                sigB: margin + 120,
                                termB: margin + 135,
                                lunghB: margin + 150,
                                collB: margin + 165
                            };

                            pdf.text(t('exportSchematic.pole'), cols.polo, headerY + 3);
                            pdf.text(t('exportSchematic.color'), cols.colore, headerY + 3);
                            pdf.text(t('exportSchematic.signA'), cols.sigA, headerY + 3);
                            pdf.text(t('exportSchematic.termA'), cols.termA, headerY + 3);
                            pdf.text(t('exportSchematic.stripA'), cols.lunghA, headerY + 3);
                            pdf.text(t('exportSchematic.connA'), cols.collA, headerY + 3);
                            pdf.text(t('exportSchematic.signB'), cols.sigB, headerY + 3);
                            pdf.text(t('exportSchematic.termB'), cols.termB, headerY + 3);
                            pdf.text(t('exportSchematic.stripB'), cols.lunghB, headerY + 3);
                            pdf.text(t('exportSchematic.connB'), cols.collB, headerY + 3);
                        } else {
                            // Layout originale con sezione
                            cols = {
                                polo: margin + 28,
                                colore: margin + 40,
                                sezione: margin + 60,
                                sigA: margin + 60,
                                termA: margin + 78,
                                lunghA: margin + 93,
                                collA: margin + 108,
                                sigB: margin + 75,
                                termB: margin + 135,
                                lunghB: margin + 150,
                                collB: margin + 165
                            };

                            pdf.text(t('exportSchematic.pole'), cols.polo, headerY + 3);
                            pdf.text(t('exportSchematic.color'), cols.colore, headerY + 3);
                            pdf.text(t('exportSchematic.section'), cols.sezione, headerY + 3);
                            pdf.text(t('exportSchematic.signA'), cols.sigA, headerY + 3);
                            pdf.text(t('exportSchematic.termA'), cols.termA, headerY + 3);
                            pdf.text(t('exportSchematic.stripA'), cols.lunghA, headerY + 3);
                            pdf.text(t('exportSchematic.connA'), cols.collA, headerY + 3);
                            pdf.text(t('exportSchematic.signB'), cols.sigB, headerY + 3);
                            pdf.text(t('exportSchematic.termB'), cols.termB, headerY + 3);
                            pdf.text(t('exportSchematic.stripB'), cols.lunghB, headerY + 3);
                            pdf.text(t('exportSchematic.connB'), cols.collB, headerY + 3);
                        }

                        currentY += 8;

                        // Righe poli
                        pdf.setFont('helvetica', 'normal');
                        pdf.setTextColor(0, 0, 0);

                        cableData.poli.forEach((polo, poloIdx) => {
                            // Alternanza colori ogni polo
                            if (poloIdx % 2 === 1) {
                                pdf.setFillColor(250, 250, 250);
                                pdf.rect(margin + 25, currentY - 2, usableWidth - 30, 8, 'F');
                            }

                            // Funzione per pulire testi - UGUALE ALLA VERSIONE B/N
                            const cleanText = (text) => {
                                if (!text) return '';
                                return String(text)
                                    .replace(/[▲▼→←↔]/g, '') // Rimuove frecce
                                    .replace(/['']/g, "'")    // Sostituisce apostrofi strani
                                    .replace(/[""]/g, '"')    // Sostituisce virgolette strane
                                    .replace(/[^\x20-\x7E]/g, '?') // Sostituisce caratteri non ASCII con ?
                                    .trim();
                            };

                            pdf.setFontSize(7);
                            pdf.text(`P${poloIdx + 1}`, cols.polo, currentY + 2);
                            pdf.text(cleanText(translateColorForDisplay(polo.colore, language)), cols.colore, currentY + 2);

                            if (hasUniformSection) {
                                // ✅ LAYOUT CON SIGLATURE
                                pdf.text(cleanText(polo.siglaturaA), cols.sigA, currentY + 2);
                                pdf.text(cleanText(polo.siglaturaB), cols.sigB, currentY + 2);
                                pdf.text(cleanText(polo.terminaleA), cols.termA, currentY + 2);
                                pdf.text(`${polo.lunghezzaA || 0}`, cols.lunghA, currentY + 2);

                                const collA = cleanText(polo.collegamentoA);
                                pdf.text(collA.length > 10 ? collA.substring(0, 10) + '...' : collA, cols.collA, currentY + 2);
                                pdf.text(cleanText(polo.terminaleB), cols.termB, currentY + 2);
                                pdf.text(`${polo.lunghezzaB || 0}`, cols.lunghB, currentY + 2);

                                const collB = cleanText(polo.collegamentoB);
                                pdf.text(collB.length > 10 ? collB.substring(0, 10) + '...' : collB, cols.collB, currentY + 2);
                            } else {
                                // Layout originale con sezione
                                pdf.text(formatSezione(polo.sezione, settings.useAWG), cols.sezione, currentY + 2);
                                pdf.text(cleanText(polo.terminaleA), cols.termA, currentY + 2);
                                pdf.text(`${polo.lunghezzaA || 0}`, cols.lunghA, currentY + 2);

                                const collA = cleanText(polo.collegamentoA);
                                pdf.text(collA.length > 12 ? collA.substring(0, 12) + '...' : collA, cols.collA, currentY + 2);
                                pdf.text(cleanText(polo.terminaleB), cols.termB, currentY + 2);
                                pdf.text(`${polo.lunghezzaB || 0}`, cols.lunghB, currentY + 2);

                                const collB = cleanText(polo.collegamentoB);
                                pdf.text(collB.length > 12 ? collB.substring(0, 12) + '...' : collB, cols.collB, currentY + 2);
                            }

                            currentY += 8;
                        });

                        currentY += 2; // Spazio dopo tabella

                    } else {
                        // ✅ CAVO STANDARD - UNA RIGA COMPLETA E BEN SPAZIATA

                        if (cableData) {
                            // Funzione per pulire testi - UGUALE ALLA VERSIONE B/N
                            const cleanText = (text) => {
                                if (!text) return '—';
                                return String(text)
                                    .replace(/[▲▼→←↔]/g, '') // Rimuove frecce
                                    .replace(/['']/g, "'")    // Sostituisce apostrofi strani
                                    .replace(/[""]/g, '"')    // Sostituisce virgolette strane
                                    .replace(/[^\x20-\x7E]/g, '?') // Sostituisce caratteri non ASCII con ?
                                    .trim();
                            };

                            // ✅ CALCOLA SIGLATURA
                            let siglaturaDisplay = '';
                            if (!settings.marcaturePerCollegamento) {
                                const normalizedCable = normalizeStandardCable(cableData);
                                const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(normalizedCable);
                                const values = [siglaturaA, siglaturaB].filter(sig => sig && String(sig).trim() !== '').map(cleanText);
                                siglaturaDisplay = values.length > 0 ? values.join(' / ') : '—';
                            } else {
                                try {
                                    const siglature = getSiglaturaPerStampa(cableData, settings);
                                    const sigArray = Array.isArray(siglature) ? siglature : [siglature];
                                    const validSigs = sigArray.filter(sig => sig && String(sig).trim() !== '').map(cleanText);
                                    siglaturaDisplay = validSigs.length > 0 ? validSigs.join(',') : '—';
                                } catch (e) {
                                    console.warn('Errore estrazione siglatura:', e);
                                    siglaturaDisplay = '—';
                                }
                            }

                            // ✅ STAMPA SIGLATURA IN GRASSETTO
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(10);
                            const siglaturaStartX = margin + 25;
                            pdf.text(siglaturaDisplay, siglaturaStartX, currentY + 3);

                            // ✅ CALCOLA POSIZIONE DINAMICA PER IL RESTO DELLE INFO
                            const siglaturaWidth = pdf.getTextWidth(siglaturaDisplay);
                            const catalogStartX = siglaturaStartX + siglaturaWidth + 6;

                            // ✅ STRINGA CON LE INFO (senza siglatura, frecce ASCII)
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(8);
                            const infoCompleta = `${cleanText(translateColorForDisplay(cableData.colore, language))}  •  ${formatSezione(cableData.sezione, settings.useAWG)}  •  ${cableData.lunghezza}mm  •  ${cleanText(cableData.terminaleA)} -> ${cleanText(cableData.terminaleB)} | ${cleanText(cableData.collegamentoA)} -> ${cleanText(cableData.collegamentoB)}`;
                            const rightEdge = margin + usableWidth - 5;
                            const catalogLabel = getCatalogLabel(cableData);
                            const minimumInfoWidth = Math.min(85, Math.max(45, rightEdge - catalogStartX));
                            let catalogWidth = 0;

                            if (catalogLabel) {
                                pdf.setFont('helvetica', 'bold');
                                pdf.setTextColor(...primaryColor);
                                const fittedCatalogLabel = fitTextToWidth(catalogLabel, Math.max(12, rightEdge - catalogStartX - minimumInfoWidth - 6));
                                if (fittedCatalogLabel) {
                                    pdf.text(fittedCatalogLabel, catalogStartX, currentY + 3);
                                    catalogWidth = pdf.getTextWidth(fittedCatalogLabel) + 6;
                                }
                            }

                            pdf.setTextColor(0, 0, 0);
                            pdf.setFont('helvetica', 'normal');
                            const infoStartX = catalogStartX + catalogWidth;
                            const fittedInfo = fitTextToWidth(infoCompleta, rightEdge - infoStartX);
                            if (fittedInfo) pdf.text(fittedInfo, infoStartX, currentY + 3);

                            currentY += 6;
                        }
                    }
                }

                // Riga di separazione delicata tra cavi
                if (idx < cableDetails.length - 1) {
                    pdf.setDrawColor(240, 240, 240);
                    pdf.setLineWidth(0.2);
                    pdf.line(margin + 10, currentY, margin + usableWidth - 10, currentY);
                    currentY += 2; // Spazio ridotto
                }
            }
        }
    });

    return currentY + 3; // Ritorna la nuova posizione Y
};

// ==================== FUNZIONE CLEAN SVG PER EXPORT ====================

const createCleanSVGForExport = (originalSvg) => {
    const clone = originalSvg.cloneNode(true);

    const extractPathPoints = (d) => {
        const numericValues = String(d || '').match(/-?\d*\.?\d+/g) || [];
        const points = [];

        for (let i = 0; i < numericValues.length - 1; i += 2) {
            points.push({
                x: parseFloat(numericValues[i]),
                y: parseFloat(numericValues[i + 1])
            });
        }

        return points;
    };

    const buildOffsetPathData = (d, direction) => {
        const points = extractPathPoints(d);

        if (points.length < 2) {
            return null;
        }

        const shiftedPoints = points.map((point, index) => {
            let normalX = 0;
            let normalY = 0;
            let contributingSegments = 0;

            const collectNormal = (startPoint, endPoint) => {
                const directionX = endPoint.x - startPoint.x;
                const directionY = endPoint.y - startPoint.y;
                const length = Math.hypot(directionX, directionY);

                if (length <= 0.001) {
                    return;
                }

                normalX += (-directionY / length) * direction;
                normalY += (directionX / length) * direction;
                contributingSegments += 1;
            };

            if (index > 0) {
                collectNormal(points[index - 1], point);
            }

            if (index < points.length - 1) {
                collectNormal(point, points[index + 1]);
            }

            if (contributingSegments === 0) {
                return point;
            }

            const averagedNormalLength = Math.hypot(normalX, normalY) || 1;
            const offsetDistance = 1;

            return {
                x: point.x + (normalX / averagedNormalLength) * offsetDistance,
                y: point.y + (normalY / averagedNormalLength) * offsetDistance
            };
        });

        return shiftedPoints.map((point, index) => (
            `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
        )).join(' ');
    };

    const earthGradientIds = new Set();
    clone.querySelectorAll('linearGradient[id]').forEach((gradientEl) => {
        const stops = Array.from(gradientEl.querySelectorAll('stop'))
            .map((stopEl) => (stopEl.getAttribute('stop-color') || stopEl.style.stopColor || '').trim().toUpperCase())
            .filter(Boolean);

        if (stops.includes('#FFD600') && stops.includes('#4CAF50')) {
            earthGradientIds.add(gradientEl.getAttribute('id'));
        }
    });

    if (earthGradientIds.size > 0) {
        clone.querySelectorAll('[stroke^="url(#"]').forEach((shapeEl) => {
            const strokeValue = shapeEl.getAttribute('stroke') || '';
            const gradientMatch = /^url\(#(.+)\)$/.exec(strokeValue);
            const gradientId = gradientMatch?.[1];

            if (!gradientId || !earthGradientIds.has(gradientId)) {
                return;
            }

            const pathData = shapeEl.getAttribute('d');
            if (!pathData) {
                return;
            }

            const yellowPathData = buildOffsetPathData(pathData, -1);
            const greenPathData = buildOffsetPathData(pathData, 1);

            if (!yellowPathData || !greenPathData) {
                return;
            }

            const strokeWidth = parseFloat(shapeEl.getAttribute('stroke-width') || shapeEl.style.strokeWidth || '1') || 1;
            const splitStrokeWidth = Math.max(strokeWidth / 2, 0.75);

            const yellowClone = shapeEl.cloneNode(true);
            yellowClone.setAttribute('d', yellowPathData);
            yellowClone.setAttribute('stroke', '#FFD600');
            yellowClone.setAttribute('stroke-width', String(splitStrokeWidth));
            yellowClone.removeAttribute('stroke-dasharray');
            yellowClone.removeAttribute('stroke-dashoffset');
            yellowClone.style.removeProperty('stroke-dasharray');
            yellowClone.style.removeProperty('stroke-dashoffset');

            const greenClone = shapeEl.cloneNode(true);
            greenClone.setAttribute('d', greenPathData);
            greenClone.setAttribute('stroke', '#4CAF50');
            greenClone.setAttribute('stroke-width', String(splitStrokeWidth));
            greenClone.removeAttribute('stroke-dasharray');
            greenClone.removeAttribute('stroke-dashoffset');
            greenClone.style.removeProperty('stroke-dasharray');
            greenClone.style.removeProperty('stroke-dashoffset');

            shapeEl.replaceWith(yellowClone, greenClone);
        });
    }

    const tooltips = clone.querySelectorAll('g:last-child');
    tooltips.forEach(g => {
        const rect = g.querySelector('rect[rx="5"]');
        const text = g.querySelector('text.text-xs.fill-gray-700');
        if (rect && text && text.textContent.includes('•')) {
            g.style.opacity = '1';
            g.style.display = 'block';
            g.removeAttribute('display');

            rect.style.opacity = '1';
            text.style.opacity = '1';
        }
    });

    clone.querySelectorAll('text').forEach(textEl => {
        const className = textEl.getAttribute('class') || '';

        // ✅ SOSTITUISCI FRECCE SOLO NEL TESTO PRINCIPALE, NON NEI TSPAN
        // Se il text non ha tspan figli, applica la sostituzione direttamente
        const tspans = textEl.querySelectorAll('tspan');
        if (tspans.length === 0 && textEl.textContent) {
            textEl.textContent = replaceArrowsForPDF(textEl.textContent);
        } else if (tspans.length > 0) {
            // Se ha tspan, cerca le frecce solo nel primo tspan (di solito la linea principale)
            tspans.forEach((tspan, index) => {
                if (tspan.textContent && (tspan.textContent.includes('▲') || tspan.textContent.includes('▼'))) {
                    tspan.textContent = replaceArrowsForPDF(tspan.textContent);
                }
            });
        }

        // Applica stili
        if (className.includes('fill-blue-800')) {
            textEl.setAttribute('fill', '#1e40af');
        } else if (className.includes('fill-gray-700')) {
            textEl.setAttribute('fill', '#374151');
        } else if (className.includes('fill-gray-600')) {
            textEl.setAttribute('fill', '#4b5563');
        } else if (className.includes('fill-gray-800')) {
            textEl.setAttribute('fill', '#1f2937');
        }

        if (className.includes('font-bold')) {
            textEl.setAttribute('font-weight', 'bold');
        } else if (className.includes('font-semibold')) {
            textEl.setAttribute('font-weight', '600');
        } else if (className.includes('font-medium')) {
            textEl.setAttribute('font-weight', '500');
        }

        if (className.includes('text-sm')) {
            textEl.setAttribute('font-size', '14px');
        } else if (className.includes('text-xs')) {
            textEl.setAttribute('font-size', '12px');
        }

        textEl.style.opacity = '1';
        textEl.style.display = 'block';
    });

    // ✅ GESTISCI I TSPAN SEPARATAMENTE (NON TOCCARE IL CONTENUTO, SOLO GLI STILI)
    clone.querySelectorAll('tspan').forEach(tspanEl => {
        // NON MODIFICARE IL CONTENUTO DEI TSPAN - mantieni la struttura multi-linea
        // Solo assicurati che siano visibili
        tspanEl.style.opacity = '1';
        tspanEl.style.display = 'inline';
    });

    // ✅ RIMUOVI CLASSI ANIMATE
    clone.querySelectorAll('.cable-path').forEach(path => {
        path.classList.remove('animated');
    });

    return clone;
};
